import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthTokens, AuthUser } from '@/types/api';

/**
 * An administrator viewing the platform as a customer.
 *
 * The administrator's own session is parked in `restore` rather than discarded, so exiting is
 * instant and cannot fail — asking them to sign in again after a support look would be a poor
 * enough experience that people would stop exiting.
 */
export interface ImpersonationState {
  sessionId: string;
  subjectEmail: string;
  actorEmail: string;
  /** Epoch ms. The token is not renewable, so this really is the end of the session. */
  expiresAt: number;
  restore: {
    accessToken: string | null;
    refreshToken: string | null;
    user: AuthUser | null;
  };
}

interface AuthState {
  /** Short-lived JWT — kept in memory only, never persisted. */
  accessToken: string | null;
  /** Long-lived rotating refresh token — persisted to survive reloads. */
  refreshToken: string | null;
  user: AuthUser | null;
  /** True once the initial session-restore attempt has completed. */
  bootstrapped: boolean;
  /** Non-null while acting as a customer. Everything the UI needs to say so, and to undo it. */
  impersonation: ImpersonationState | null;

  setSession: (tokens: AuthTokens, user: AuthUser) => void;
  updateTokens: (tokens: AuthTokens) => void;
  setUser: (user: AuthUser) => void;
  clear: () => void;
  setBootstrapped: () => void;
  beginImpersonation: (state: ImpersonationState, accessToken: string) => void;
  endImpersonation: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      bootstrapped: false,
      impersonation: null,

      setSession: (tokens, user) =>
        set({ accessToken: tokens.access_token, refreshToken: tokens.refresh_token, user }),
      updateTokens: (tokens) =>
        set({ accessToken: tokens.access_token, refreshToken: tokens.refresh_token }),
      setUser: (user) => set({ user }),
      clear: () => set({ accessToken: null, refreshToken: null, user: null, impersonation: null }),
      setBootstrapped: () => set({ bootstrapped: true }),

      beginImpersonation: (impersonation, accessToken) =>
        set({
          accessToken,
          // Deliberately cleared. The refresh token belongs to the *administrator*, and leaving
          // it in place would let the silent-refresh path mint a fresh admin access token the
          // moment the impersonation token expired — restoring full privileges underneath a
          // banner still claiming to be the customer. It is parked in `restore` instead.
          refreshToken: null,
          impersonation,
        }),

      endImpersonation: () =>
        set((state) =>
          state.impersonation === null
            ? state
            : {
                accessToken: state.impersonation.restore.accessToken,
                refreshToken: state.impersonation.restore.refreshToken,
                user: state.impersonation.restore.user,
                impersonation: null,
              },
        ),
    }),
    {
      name: 'iap-auth',
      // Persist only the refresh token and user; the access token stays in memory.
      // Impersonation is persisted too: a reload mid-session would otherwise leave the
      // administrator holding a customer's view with the banner gone and no way back.
      partialize: (state) => ({
        refreshToken: state.refreshToken,
        user: state.user,
        impersonation: state.impersonation,
      }),
    },
  ),
);

export const authStore = useAuthStore;
